'use client'

import { useMemo, useState } from 'react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { isoWeek, isoMonth } from '@/lib/dates'
import { TOOLTIP_STYLE, AXIS_STYLE, CHART_COLORS } from './chartConfig'
import type { Task } from '@/types'

export default function ThroughputChart({ tasks }: { tasks: Task[] }) {
  const [mode, setMode] = useState<'week' | 'month'>('week')

  const data = useMemo(() => {
    const done = tasks.filter(t => t.stage === 'Done' && t.last_stage)
    const groupFn = mode === 'week' ? isoWeek : isoMonth
    const groups: Record<string, number> = {}
    done.forEach(t => {
      const k = groupFn(t.last_stage)
      groups[k] = (groups[k] || 0) + 1
    })
    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-12)
      .map(([key, count]) => ({
        label: mode === 'week' ? key.slice(5) : key,
        count,
      }))
  }, [tasks, mode])

  return (
    <div>
      <div className="flex gap-1 mb-3">
        {(['week', 'month'] as const).map(m => (
          <button key={m} onClick={() => setMode(m)}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${mode === m ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'}`}>
            {m === 'week' ? 'Weekly' : 'Monthly'}
          </button>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data}>
          <XAxis dataKey="label" {...AXIS_STYLE} />
          <YAxis {...AXIS_STYLE} width={30} />
          <Tooltip {...TOOLTIP_STYLE} />
          <Bar dataKey="count" fill={CHART_COLORS.primary} radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
